import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/auth/authService';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Avatar,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import {
  Person,
  Edit,
  Save,
  Cancel,
  Business,
  Email,
  Shield,
  AccountCircle,
  Assignment,
  LocationOn
} from '@mui/icons-material';

const UserProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [userPermissions, setUserPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: ''
  });

  // Initialize form data when user data is available
  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || user.full_name || '',
        email: user.email || ''
      });
    }
  }, [user]);

  // Fetch user permissions
  useEffect(() => {
    const fetchUserPermissions = async () => {
      const userPermissionScope = user?.permissionScope || user?.permission_scope;
      console.log('UserProfilePage - User object:', user);
      console.log('UserProfilePage - Permission scope:', userPermissionScope);
      
      if (user) {
        // For organization-level users, show organization access
        if (userPermissionScope === 'organization') {
          setPermissionsLoading(true);
          try {
            const orgPermissions = [{
              id: 'org_access',
              type: 'Organization',
              name: `Full ${user.organizationName || 'Organization'} Access`,
              permissionType: 'write',
              category: 'Location'
            }];
            
            // Fetch discipline permissions from backend
            const response = await authService.getUserPermissions(user.id);
            console.log('UserProfilePage - Organization user permissions response:', response);
            
            if (response.success && response.permissions) {
              // Add discipline permissions if available
              if (response.permissions.disciplines && response.permissions.disciplines.length > 0) {
                const disciplinePermissions = response.permissions.disciplines.map(discipline => ({
                  id: discipline.id,
                  type: 'Discipline',
                  name: discipline.name,
                  permissionType: 'access',
                  category: 'Discipline'
                }));
                orgPermissions.push(...disciplinePermissions);
              }
              
              if (response.permissions.sub_disciplines && response.permissions.sub_disciplines.length > 0) {
                const subDisciplinePermissions = response.permissions.sub_disciplines.map(subDiscipline => ({
                  id: `sub_${subDiscipline}`,
                  type: 'Sub-Discipline',
                  name: subDiscipline,
                  permissionType: 'access',
                  category: 'Discipline'
                }));
                orgPermissions.push(...subDisciplinePermissions);
              }
            }
            
            setUserPermissions(orgPermissions);
          } catch (error) {
            console.error('Error fetching organization user permissions:', error);
            // Fallback to just showing organization access
            setUserPermissions([{
              id: 'org_access',
              type: 'Organization',
              name: `Full ${user.organizationName || 'Organization'} Access`,
              permissionType: 'write',
              category: 'Location'
            }]);
          } finally {
            setPermissionsLoading(false);
          }
          return;
        }
        
        // For network/section-level users, fetch specific permissions
        if (userPermissionScope === 'network_section') {
          setPermissionsLoading(true);
          try {
            const response = await authService.getUserPermissions(user.id);
            console.log('UserProfilePage - Permissions response:', response);
            if (response.success && response.permissions) {
              const allPermissions = [];
              
              // Transform location permissions
              const locationPermissions = (response.permissions.locations || []).map(permission => {
                if (permission.network_id && !permission.section_id) {
                  return {
                    id: permission.network_id,
                    type: 'Network',
                    name: permission.network_name || `Network ${permission.network_id}`,
                    permissionType: permission.permission_type || 'write',
                    category: 'Location'
                  };
                } else if (permission.section_id) {
                  return {
                    id: permission.section_id,
                    type: 'Section',
                    name: permission.section_name || `Section ${permission.section_id}`,
                    permissionType: permission.permission_type || 'write',
                    category: 'Location',
                    networkName: permission.section_network_name
                  };
                }
                return null;
              }).filter(Boolean);

              // Transform discipline permissions
              const disciplinePermissions = (response.permissions.disciplines || []).map(discipline => ({
                id: discipline.id,
                type: 'Discipline',
                name: discipline.name,
                permissionType: 'access',
                category: 'Discipline'
              }));

              // Transform sub-discipline permissions
              const subDisciplinePermissions = (response.permissions.sub_disciplines || []).map(subDiscipline => ({
                id: `sub_${subDiscipline}`,
                type: 'Sub-Discipline',
                name: subDiscipline,
                permissionType: 'access',
                category: 'Discipline'
              }));

              allPermissions.push(...locationPermissions, ...disciplinePermissions, ...subDisciplinePermissions);
              
              console.log('UserProfilePage - Transformed permissions:', allPermissions);
              setUserPermissions(allPermissions);
            }
          } catch (error) {
            console.error('Error fetching user permissions:', error);
          } finally {
            setPermissionsLoading(false);
          }
        }
      }
    };

    fetchUserPermissions();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const response = await authService.updateProfile(formData);
      
      if (response.success) {
        setSuccessMessage('Profile updated successfully!');
        setIsEditing(false);
        
        // Refresh user data
        await refreshUser();
      } else {
        setErrors({
          general: response.error || 'Failed to update profile. Please try again.'
        });
      }
    } catch (error) {
      console.error('Profile update failed:', error);
      setErrors({
        general: error.message || 'Failed to update profile. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original user data
    if (user) {
      setFormData({
        fullName: user.fullName || user.full_name || '',
        email: user.email || ''
      });
    }
    setErrors({});
    setSuccessMessage('');
    setIsEditing(false);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'system_admin': return 'error';
      case 'manager': return 'primary';
      case 'inspector': return 'warning';
      case 'viewer': return 'info';
      default: return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'system_admin': return 'System Admin';
      case 'manager': return 'Manager';
      case 'inspector': return 'Inspector';
      case 'viewer': return 'Viewer';
      default: return role;
    }
  };

  const getPermissionScopeLabel = (scope) => {
    switch (scope) {
      case 'organization': return 'Organization-wide Access';
      case 'network_section': return 'Network/Section Level Access';
      default: return scope;
    }
  };

  // Helper function to get initials from full name
  const getInitials = (fullName) => {
    if (!fullName) return '';
    const names = fullName.trim().split(' ');
    if (names.length >= 2) {
      return names[0].charAt(0) + names[names.length - 1].charAt(0);
    }
    return names[0].charAt(0);
  };

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Loading user profile...
          </Typography>
        </Stack>
      </Container>
    );
  }

  const userFullName = user.fullName || user.full_name || '';

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Page Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <AccountCircle sx={{ fontSize: 28, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" fontWeight={600}>
            User Profile
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your personal information and account settings
          </Typography>
        </Box>
      </Stack>

      <Paper sx={{ p: 3, borderRadius: 1 }}>
        {/* Profile Header */}
        <Box display="flex" alignItems="center" mb={4}>
          <Avatar 
            sx={{ 
              width: 80, 
              height: 80, 
              mr: 3,
              bgcolor: 'primary.main',
              fontSize: '2rem'
            }}
          >
            {getInitials(userFullName)}
          </Avatar>
          <Box flex={1}>
            <Typography variant="h4" gutterBottom>
              {userFullName || 'User'}
            </Typography>
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              {user.permissionScope && (
                <Chip 
                  label={getPermissionScopeLabel(user.permissionScope)}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          </Box>
          <Button
            variant={isEditing ? "outlined" : "contained"}
            startIcon={isEditing ? <Cancel /> : <Edit />}
            onClick={isEditing ? handleCancel : () => setIsEditing(true)}
            disabled={isLoading}
          >
            {isEditing ? 'Cancel' : 'Edit Profile'}
          </Button>
        </Box>

        {/* Success Message */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        {/* General Error Message */}
        {errors.general && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errors.general}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Personal Information */}
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 1 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Person color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Personal Information
                  </Typography>
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      disabled={!isEditing || isLoading}
                      error={!!errors.fullName}
                      helperText={errors.fullName}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={!isEditing || isLoading}
                      error={!!errors.email}
                      helperText={errors.email}
                      margin="normal"
                      InputProps={{
                        startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />
                      }}
                    />
                  </Grid>
                </Grid>

                {isEditing && (
                  <Box mt={3} display="flex" gap={2}>
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={handleSave}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={handleCancel}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Organization & Role Information */}
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 1 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Business color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Organization & Role
                  </Typography>
                </Stack>

                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Organization
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {user.organizationName || 'N/A'}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Role
                  </Typography>
                  <Chip 
                    label={getRoleLabel(user.role)}
                    color={getRoleColor(user.role)}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>

                {user.permissionScope && (
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      Access Level
                    </Typography>
                    <Typography variant="body1">
                      {getPermissionScopeLabel(user.permissionScope)}
                    </Typography>
                  </Box>
                )}

                {user.createdAt && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Member Since
                    </Typography>
                    <Typography variant="body1">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Account Security */}
            <Card sx={{ mt: 2, borderRadius: 1 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Shield color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Account Security
                  </Typography>
                </Stack>

                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Email Status
                  </Typography>
                  <Chip 
                    label={user.emailVerified ? "Verified" : "Unverified"}
                    color={user.emailVerified ? "success" : "warning"}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>

                {user.lastLogin && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Last Sign In
                    </Typography>
                    <Typography variant="body1">
                      {new Date(user.lastLogin).toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Access Permissions - Show for all users */}
            {user && (
              <Card sx={{ mt: 2, borderRadius: 1 }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                    <Assignment color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      Access Permissions
                    </Typography>
                  </Stack>

                  {permissionsLoading ? (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Loading permissions...
                      </Typography>
                    </Stack>
                  ) : userPermissions.length > 0 ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        You have access to the following:
                      </Typography>
                      <Stack spacing={2}>
                        {/* Group permissions by category */}
                        {['Location', 'Discipline'].map(category => {
                          const categoryPermissions = userPermissions.filter(p => p.category === category);
                          if (categoryPermissions.length === 0) return null;
                          
                          return (
                            <Box key={category}>
                              <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                {category} Permissions
                              </Typography>
                              <Stack spacing={1}>
                                {categoryPermissions.map((permission, index) => (
                                  <Box 
                                    key={`${category}-${index}`}
                                    sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      gap: 1,
                                      p: 1.5,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 1,
                                      bgcolor: 'background.paper'
                                    }}
                                  >
                                    {permission.category === 'Location' ? (
                                      <LocationOn 
                                        color={permission.type === 'Network' ? 'primary' : 'secondary'} 
                                        sx={{ fontSize: 20 }}
                                      />
                                    ) : (
                                      <Assignment 
                                        color="primary"
                                        sx={{ fontSize: 20 }}
                                      />
                                    )}
                                    <Box flex={1}>
                                      <Typography variant="body2" fontWeight={600}>
                                        {permission.name}
                                        {permission.networkName && (
                                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                            ({permission.networkName} Network)
                                          </Typography>
                                        )}
                                      </Typography>
                                      <Box display="flex" gap={1} alignItems="center" mt={0.5}>
                                        <Chip
                                          label={permission.type}
                                          size="small"
                                          color={
                                            permission.type === 'Network' ? 'primary' : 
                                            permission.type === 'Section' ? 'secondary' : 'info'
                                          }
                                          variant="outlined"
                                        />
                                        {permission.category === 'Location' && (
                                          <Chip
                                            label={permission.permissionType === 'write' ? 'Read/Write' : 'Read Only'}
                                            size="small"
                                            color={permission.permissionType === 'write' ? 'success' : 'info'}
                                            variant="outlined"
                                          />
                                        )}
                                      </Box>
                                    </Box>
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No specific permissions assigned. Contact your administrator for access.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default UserProfilePage; 